import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/internal/operators/takeUntil';
import { Subject } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperInstance, ChartWrapperService } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { formatDisplayText } from '../../utils/text-case.utils';

/**
 * Generic, config-driven drill-down (tree table) chart.
 *
 * Unlike the legacy `DrillDownComponent` (which hardcodes Customer/Engine/Spec
 * levels and a `customer_spec` filter), this component is fully data-driven and
 * works for ANY hierarchy. It is the type the self-service builder emits as
 * `generic-drill-down`.
 *
 * Expected `this.data` (built by the persistence layer / computeSelfServiceData):
 *   {
 *     Data:  DrillNode[]        // nested tree — { name, value, children?, ... }
 *     levelColumns: string[]    // ordered dimension columns → one drill level each
 *     measureColumns: string[]  // numeric columns shown (and summed) per row
 *     columnLabels?: {key:label}
 *   }
 * If only flat `options` rows + `levelColumns`/`measureColumns` are supplied the
 * component builds the tree itself, so it also works from raw table-style data.
 */
type DrillNode = {
  name: string;
  level: number;
  depth: number;
  expanded?: boolean;
  hasChildren?: boolean;
  children?: DrillNode[];
  parent?: DrillNode | null;
  values: Record<string, number>;
  /** Ordered dimension values from the root down to this node — used as the
   *  cross-filter payload when a row is clicked. */
  path: Record<string, string>;
};

@Component({
  selector: 'lib-generic-drill-down',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
  templateUrl: './generic-drill-down.component.html',
  styleUrl: './generic-drill-down.component.scss',
})
export class GenericDrillDownComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data!: any;
  /**
   * Emitted on row click so a HOST (e.g. the dashboard-builder arrange view)
   * can drive its own cross-filter mechanism. Carries the CLICKED level's
   * column + value, or null when the row is toggled off. The internal
   * ChartFilterHandlerService publish (published dashboard) is separate and
   * only runs when data.filterKey is set — this output is the arrange view's
   * cross-filter path.
   */
  @Output() filterSelect = new EventEmitter<{ column: string; value: string } | null>();
  @Input() set widgetId(val: string) {
    this._widgetId = val;
  }
  get widgetId(): string {
    return this._widgetId || this.data?.widgetId;
  }
  private _widgetId!: string;

  isLoading = true;
  filter: any;

  /** Ordered dimension column keys — one drill level each. */
  levelColumns: string[] = [];
  /** Numeric measure column keys shown per row. */
  measureColumns: string[] = [];
  columnLabels: Record<string, string> = {};

  /** The built hierarchy and the flattened, currently-visible rows. */
  private tree: DrillNode[] = [];
  flattenedRows: DrillNode[] = [];
  grandTotals: Record<string, number> = {};

  private headerCase: string = 'capital';
  private selectedPathKey: string | null = null;

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private chartFilterHandler = inject(ChartFilterHandlerService);
  private reflowSub?: Subscription;
  private unSubscribe$: Subject<void> = new Subject();
  /** Set once the initial load has run, so ngOnChanges only re-loads on later
   *  @Input() data swaps (e.g. the arrange view handing us a cross-filtered
   *  dataset), not the first change that fires before ngAfterViewInit. */
  private viewReady = false;

  constructor(private configService: ConfigService) {}

  /**
   * Re-ingest when the host swaps in a new `data` object. This is ONLY for the
   * dashboard-builder ARRANGE view, which rebuilds `data` (with a cross-filtered
   * `selfService` payload, tagged with a `__key`) whenever active cross-filters
   * change — there is no wrapper/global-filter reload in that view, so the
   * component must react to the @Input itself.
   *
   * The PUBLISHED view is deliberately excluded: there, filtering is driven by
   * ChartWrapperService's onReload callback (global filter service), and a
   * `data`-triggered reload here would double-fire / race with a stale
   * `this.filter`. Published payloads have no `__key`, so this early-returns.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewReady) return;
    if (!this.data?.__key) return; // published view → wrapper drives reload
    if (changes['data'] && !changes['data'].firstChange) {
      this.headerCase = (this.data?.textCase || 'capital') as string;
      this.reload(this.filter);
    }
  }

  ngAfterViewInit(): void {
    this.headerCase = (this.data?.textCase || 'capital') as string;

    const initialFilters = this.wrapper.attach(
      this.data,
      {
        onReload: (filters) => this.reload(filters),
        getChart: () => null,
        getSeriesNames: () => [],
        getCategories: () => [],
      },
      'category',
    );

    if (this.data?.callApiInDefault !== false) {
      this.reload(initialFilters ?? undefined);
    } else if (initialFilters) {
      this.reload(initialFilters);
    }

    this.viewReady = true;
  }

  private reload(filters?: any): void {
    this.filter = filters;
    this.isLoading = true;
    this.configService
      .getConfig(this.data?.apiUrl, this.data, filters, this.data?.method || 'post', true)
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: any) => {
          this.ingest(res);
          this.isLoading = false;
        },
        error: (err: any) => {
          this.isLoading = false;
          console.error('Error loading drill-down data', err);
        },
      });
  }

  /** Accept either a pre-built tree ({ Data }) or flat rows ({ options }). */
  private ingest(res: any): void {
    this.resolveColumns(res);

    let tree: DrillNode[] | null = null;
    const preBuilt = res?.Data ?? res?.data ?? null;
    if (Array.isArray(preBuilt) && preBuilt.length && preBuilt[0]?.children !== undefined) {
      tree = this.normalizeTree(preBuilt, 1, 0, null, {});
    }

    if (!tree) {
      const rows: any[] = res?.options || res?.rows || (Array.isArray(res) ? res : []) || [];
      tree = this.buildTree(rows);
    }

    this.tree = tree || [];
    this.grandTotals = this.sumNodes(this.tree);
    this.rebuildLabels();
    this.flatten();
  }

  private resolveColumns(res: any): void {
    // Level + measure columns come from the config (self-service) or the API
    // payload. If neither is present, infer from the first flat row: string
    // columns become levels, numeric columns become measures.
    this.levelColumns =
      res?.levelColumns || this.data?.levelColumns || res?.dimensions || this.data?.dimensions || [];
    this.measureColumns =
      res?.measureColumns || this.data?.measureColumns || res?.measures || this.data?.measures || [];

    if ((!this.levelColumns.length || !this.measureColumns.length)) {
      const sample = (res?.options || res?.rows || [])[0];
      if (sample && typeof sample === 'object') {
        const levels: string[] = [];
        const measures: string[] = [];
        for (const [key, val] of Object.entries(sample)) {
          if (this.isNumeric(val)) measures.push(key);
          else levels.push(key);
        }
        if (!this.levelColumns.length) this.levelColumns = levels;
        if (!this.measureColumns.length) this.measureColumns = measures;
      }
    }
  }

  private rebuildLabels(): void {
    const provided = this.data?.columnLabels || {};
    const all = [...this.levelColumns, ...this.measureColumns];
    this.columnLabels = Object.fromEntries(
      all.map((key) => [key, provided[key] || formatDisplayText(key, this.headerCase as any)]),
    );
  }

  // ─── Tree construction ─────────────────────────────────────────────────────

  /** Group flat rows into a nested tree by the ordered level columns, summing
   *  the measure columns at every node. */
  private buildTree(rows: any[]): DrillNode[] {
    if (!this.levelColumns.length) return [];

    const rootChildren: DrillNode[] = [];
    // Map keyed by composite path → node, so siblings across rows merge.
    // A "|" separator between level values keeps "AB|C" distinct from "A|BC".
    const index = new Map<string, DrillNode>();
    const lastLevel = this.levelColumns.length - 1;

    for (const row of rows) {
      let siblings = rootChildren;
      let parent: DrillNode | null = null;
      let pathKey = '';
      const path: Record<string, string> = {};

      this.levelColumns.forEach((levelCol, li) => {
        const name = this.safeText(row[levelCol]);
        path[levelCol] = name;
        pathKey = pathKey ? `${pathKey}${name}` : name;

        let node = index.get(pathKey);
        if (!node) {
          node = {
            name,
            level: li + 1,
            depth: li,
            expanded: false,
            children: li < lastLevel ? [] : undefined,
            parent,
            values: {},
            path: { ...path },
          };
          this.measureColumns.forEach((m) => (node!.values[m] = 0));
          index.set(pathKey, node);
          siblings.push(node);
        }

        this.measureColumns.forEach((m) => {
          node!.values[m] += this.toNumber(row[m]);
        });

        parent = node;
        // Descend into this node's own children array for the next level.
        siblings = node.children ?? [];
      });
    }

    this.markHasChildren(rootChildren);
    return rootChildren;
  }

  /** Normalize an API-provided nested tree into DrillNode shape. */
  private normalizeTree(
    nodes: any[],
    level: number,
    depth: number,
    parent: DrillNode | null,
    parentPath: Record<string, string>,
  ): DrillNode[] {
    const levelCol = this.levelColumns[depth] || `level${depth}`;
    return nodes.map((n) => {
      const name = this.safeText(n?.name ?? n?.label ?? n?.[levelCol]);
      const path = { ...parentPath, [levelCol]: name };
      const node: DrillNode = {
        name,
        level,
        depth,
        expanded: !!n?.expanded,
        parent,
        values: {},
        path,
        children: undefined,
      };
      this.measureColumns.forEach((m) => (node.values[m] = this.toNumber(n?.[m] ?? n?.values?.[m])));
      const kids = n?.children;
      if (Array.isArray(kids) && kids.length) {
        node.children = this.normalizeTree(kids, level + 1, depth + 1, node, path);
      }
      node.hasChildren = !!node.children?.length;
      return node;
    });
  }

  private markHasChildren(nodes: DrillNode[]): void {
    for (const node of nodes) {
      node.hasChildren = !!node.children?.length;
      if (node.children) this.markHasChildren(node.children);
    }
  }

  private sumNodes(nodes: DrillNode[]): Record<string, number> {
    const totals: Record<string, number> = {};
    this.measureColumns.forEach((m) => (totals[m] = 0));
    for (const node of nodes) {
      this.measureColumns.forEach((m) => (totals[m] += node.values[m] || 0));
    }
    return totals;
  }

  // ─── Flatten / expand ──────────────────────────────────────────────────────

  private flatten(): void {
    const result: DrillNode[] = [];
    const walk = (nodes: DrillNode[]): void => {
      for (const node of nodes) {
        result.push(node);
        if (node.expanded && node.children?.length) {
          walk(node.children);
        }
      }
    };
    walk(this.tree);
    this.flattenedRows = result;
  }

  toggleExpand(row: DrillNode, event?: Event): void {
    event?.stopPropagation();
    if (!row.hasChildren) return;
    row.expanded = !row.expanded;
    this.flatten();
  }

  expandAll(): void {
    this.setExpandedRecursive(this.tree, true);
    this.flatten();
  }

  collapseAll(): void {
    this.setExpandedRecursive(this.tree, false);
    this.flatten();
  }

  private setExpandedRecursive(nodes: DrillNode[], expanded: boolean): void {
    for (const node of nodes) {
      if (node.hasChildren) node.expanded = expanded;
      if (node.children) this.setExpandedRecursive(node.children, expanded);
    }
  }

  // ─── Interaction (cross-filter on row click) ───────────────────────────────

  onRowClick(row: DrillNode): void {
    const key = this.pathKeyOf(row);
    this.selectedPathKey = this.selectedPathKey === key ? null : key;

    // Cross-filter publish. A drill-down row lives at a specific level, so it
    // must publish the CLICKED level's column — not a single chart-wide
    // filterKey that may point at a different level. e.g. clicking a level-1
    // "region" row publishes { region: 'West' }; clicking a level-2 "product"
    // row publishes { product: 'X' }. The listener side (applyFilters) matches
    // row[column] === value, so the published KEY must be a real column name
    // and the VALUE the clicked cell — which is exactly row.path[levelColumn].
    const levelColumn = this.levelColumns[row.depth];
    if (!levelColumn) return;

    const clickedValue = row.path[levelColumn];

    // Host-driven cross-filter hook (dashboard-builder arrange view). Emit the
    // clicked level's column+value, or null on toggle-off, so the host can
    // narrow its listener cards. This is the arrange view's ONLY cross-filter
    // path (it does not use GlobalFilterService).
    this.filterSelect.emit(
      this.selectedPathKey ? { column: levelColumn, value: String(clickedValue ?? '') } : null,
    );

    // Internal global-filter publish is for the PUBLISHED dashboard, which sets
    // data.filterKey on the widget entry. The arrange view leaves it unset and
    // drives cross-filter via the output above, so skip the global publish there
    // (avoids writing stray keys into GlobalFilterService).
    if (!this.data?.filterKey) return;

    // Toggle OFF (deselect): pass an EMPTY ARRAY, not an empty object.
    // ChartFilterHandlerService.updateFilterSelection treats an empty array as
    // explicit deselection (resets the filter key → listeners reload unfiltered).
    // An empty OBJECT falls through its extraction path, sets the value to the
    // object itself → String({}) = "[object Object]", so listeners filter on a
    // value that matches no row and go BLANK. That was the deselect bug.
    if (!this.selectedPathKey) {
      this.chartFilterHandler.updateFilterSelection(this.data, [], []);
      return;
    }

    // Publish the clicked level's value under BOTH the clicked column name and
    // the configured filterKey, so updateFilterSelection can extract it whether
    // the listener wiring keys off the level column or the chart's filterKey.
    const payload = { [levelColumn]: clickedValue, [this.data.filterKey]: clickedValue };

    this.chartFilterHandler.updateFilterSelection(this.data, payload, payload);
  }

  isRowSelected(row: DrillNode): boolean {
    return this.selectedPathKey === this.pathKeyOf(row);
  }

  private pathKeyOf(row: DrillNode): string {
    return this.levelColumns
      .slice(0, row.depth + 1)
      .map((c) => row.path[c] ?? '')
      .join('');
  }

  // ─── Template helpers ──────────────────────────────────────────────────────

  /**
   * The tree always fills its host (100%) and scrolls INTERNALLY. The HOST's
   * height is what's sized per context — never a 20vh default here:
   *   - Published card: the grid derives the cell height from the widget's
   *     config `height` (heightToRows), and dashboard CSS chains that height
   *     down to this component (.card-body:has(lib-generic-drill-down)).
   *   - Arrange card / builder preview: a fixed height on the host wrapper
   *     (.dashboard-drilldown-live / .drilldown-preview-live).
   * Filling 100% (rather than a self-set px height) keeps the tree flush to the
   * card with no empty gap below it.
   */
  get containerHeight(): string {
    return '100%';
  }

  /** The first level column carries the expand/label tree cell. */
  get labelColumn(): string {
    return this.levelColumns[0] || 'name';
  }

  labelFor(key: string): string {
    return this.columnLabels[key] || key;
  }

  getIndentPadding(depth: number): string {
    return `${depth * 18}px`;
  }

  formatMeasure(value: number | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) return '';
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }

  showDivider(column: string): boolean {
    const selective = this.data?.dividerColumns;
    if (Array.isArray(selective) && selective.length > 0) return selective.includes(column);
    return this.data?.showColumnDividers === true;
  }

  // ─── Utils ─────────────────────────────────────────────────────────────────

  private isNumeric(val: unknown): boolean {
    if (typeof val === 'number') return Number.isFinite(val);
    if (typeof val === 'string' && val.trim() !== '') return Number.isFinite(Number(val));
    return false;
  }

  private toNumber(val: unknown): number {
    const n = Number(val);
    return Number.isFinite(n) ? n : 0;
  }

  private safeText(val: unknown): string {
    if (val === null || val === undefined || val === '') return '(blank)';
    return String(val);
  }

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }
}
